const FRAME_FILL_TOLERANCE_PX = 1;

const setImportantStyle = (
  style: CSSStyleDeclaration,
  property: string,
  value: string,
): void => {
  if (
    style.getPropertyValue(property) === value &&
    style.getPropertyPriority(property) === 'important'
  ) {
    return;
  }
  style.setProperty(property, value, 'important');
};

const frameFillsTarget = (target: HTMLElement, frame: HTMLElement): boolean => {
  const targetRect = target.getBoundingClientRect();
  const frameRect = frame.getBoundingClientRect();
  if (targetRect.width <= 0 || targetRect.height <= 0) return false;
  return (
    frameRect.left <= targetRect.left + FRAME_FILL_TOLERANCE_PX &&
    frameRect.top <= targetRect.top + FRAME_FILL_TOLERANCE_PX &&
    frameRect.right + FRAME_FILL_TOLERANCE_PX >= targetRect.right &&
    frameRect.bottom + FRAME_FILL_TOLERANCE_PX >= targetRect.bottom
  );
};

export type BasisTheoryCardInteractiveReadyGate = {
  observeFrame(frame: HTMLElement): void;
  providerReady(): void;
  stop(): void;
};

/**
 * A provider `ready` event only proves that Basis Theory initialized. Paypercut
 * fields are interactively ready once the secure iframe also fills the surface
 * that receives merchant clicks. The important dimensions override the
 * provider's fixed 29px iframe default without reaching into its origin.
 */
export const createBasisTheoryCardInteractiveReadyGate = (
  target: HTMLElement,
  onInteractiveReady: () => void,
): BasisTheoryCardInteractiveReadyGate => {
  let active = true;
  let providerIsReady = false;
  let announced = false;
  let frame: HTMLElement | undefined;

  const check = (): void => {
    if (!active || announced || !providerIsReady || !frame) return;
    const ready = frameFillsTarget(target, frame);
    if (!ready) return;
    announced = true;
    onInteractiveReady();
  };

  const resizeObserver =
    typeof ResizeObserver === 'undefined'
      ? undefined
      : new ResizeObserver(() => {
          check();
        });
  resizeObserver?.observe(target);

  return {
    observeFrame: (nextFrame) => {
      if (!active) return;
      if (frame !== nextFrame) {
        if (frame) resizeObserver?.unobserve(frame);
        frame = nextFrame;
        resizeObserver?.observe(frame);
      }
      setImportantStyle(frame.style, 'width', '100%');
      setImportantStyle(frame.style, 'height', '100%');
      setImportantStyle(frame.style, 'display', 'block');
      check();
    },
    providerReady: () => {
      if (!active || announced) return;
      providerIsReady = true;
      check();
    },
    stop: () => {
      if (!active) return;
      active = false;
      resizeObserver?.disconnect();
    },
  };
};
